import React, { useState } from 'react';
import { 
  ArrowLeft, Upload, Camera, Edit, Save, X, Clock, Users, Euro, 
  Calendar, AlertCircle, Check, MapPin, Star, Settings, Trash2, Plus
} from 'lucide-react';

const ServiceDetail = ({ service, partner, onBack, onSave }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [editMode, setEditMode] = useState({
    general: false,
    images: false,
    availability: false,
    conditions: false
  });
  const [editingService, setEditingService] = useState({ ...service });
  const [editingAvailability, setEditingAvailability] = useState({ ...service.availability });
  const [serviceImages, setServiceImages] = useState([
    {
      id: 'img-001',
      url: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Cdefs%3E%3ClinearGradient id='grad1' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%231e3a5f;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23c9a961;stop-opacity:0.8' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='300' fill='url(%23grad1)' /%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='white' font-size='18' font-family='Arial'%3E${service.name} - Photo 1%3C/text%3E%3C/svg%3E`,
      isPrimary: true,
      alt: `${service.name} - Photo principale`,
      title: 'Photo principale'
    },
    {
      id: 'img-002',
      url: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Cdefs%3E%3ClinearGradient id='grad2' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%234a90a4;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%2350c9c3;stop-opacity:0.8' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='300' fill='url(%23grad2)' /%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='white' font-size='18' font-family='Arial'%3E${service.name} - Photo 2%3C/text%3E%3C/svg%3E`,
      isPrimary: false,
      alt: `${service.name} - Photo 2`,
      title: 'Équipement'
    },
    {
      id: 'img-003',
      url: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Cdefs%3E%3ClinearGradient id='grad3' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ff6b35;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23f7971e;stop-opacity:0.8' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='300' fill='url(%23grad3)' /%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='white' font-size='18' font-family='Arial'%3E${service.name} - Photo 3%3C/text%3E%3C/svg%3E`,
      isPrimary: false,
      alt: `${service.name} - Photo 3`,
      title: 'Ambiance'
    }
  ]);

  const handleSave = (tabName) => {
    if (tabName === 'availability') {
      setEditingService({
        ...editingService,
        availability: editingAvailability
      });
    }
    
    onSave(editingService);
    setEditMode({
      ...editMode,
      [tabName]: false
    });
  };

  const handleCancel = (tabName) => {
    setEditingService({ ...service });
    setEditingAvailability({ ...service.availability });
    setEditMode({
      ...editMode,
      [tabName]: false
    });
  };

  const handleToggleEdit = (tabName) => {
    setEditMode({
      ...editMode,
      [tabName]: !editMode[tabName]
    });
  };

  const handleAvailabilityChange = (day, field, value) => {
    setEditingAvailability({
      ...editingAvailability,
      [day]: {
        ...editingAvailability[day],
        [field]: value
      }
    });
  };

  const handleSlotsChange = (day, slots) => {
    setEditingAvailability({
      ...editingAvailability,
      [day]: {
        ...editingAvailability[day],
        slots: slots.split(',').map(s => s.trim()).filter(s => s)
      }
    });
  };

  const handleImageUpload = () => {
    // Logique d'upload d'image
    console.log('Upload image');
  };

  const handleSetPrimaryImage = (imageId) => {
    setServiceImages(prev => prev.map(img => ({
      ...img,
      isPrimary: img.id === imageId
    })));
  };

  const handleDeleteImage = (imageId) => {
    setServiceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const days = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' }
  ];

  return (
    <div className="service-detail min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
              <span>Retour aux prestations</span>
            </button>
            <div className="border-l border-gray-300 pl-4">
              <h1 className="text-2xl font-bold text-gray-800">{service.name}</h1>
              <p className="text-gray-600 mt-1">{partner.name} • {partner.location}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {Object.values(editMode).some(mode => mode) ? (
              <>
                <button
                  onClick={() => handleSave(activeTab)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Save size={18} />
                  <span>Sauvegarder</span>
                </button>
                <button
                  onClick={() => handleCancel(activeTab)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <X size={18} />
                  <span>Annuler</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => handleToggleEdit(activeTab)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit size={18} />
                <span>Modifier</span>
              </button>
            )}
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Euro className="text-blue-600" size={20} />
              <span className="text-xs text-blue-600">Prix</span>
            </div>
            <div className="text-xl font-bold text-gray-800">€{service.price.toLocaleString()}</div>
            <div className="text-sm text-gray-600">par prestation</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-green-600" size={20} />
              <span className="text-xs text-green-600">Durée</span>
            </div>
            <div className="text-xl font-bold text-green-600">{service.duration}</div>
            <div className="text-sm text-gray-600">durée totale</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-yellow-600" size={20} />
              <span className="text-xs text-yellow-600">Capacité</span>
            </div>
            <div className="text-xl font-bold text-yellow-600">{service.minGuests}-{service.maxGuests}</div>
            <div className="text-sm text-gray-600">personnes</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-purple-600" size={20} />
              <span className="text-xs text-purple-600">Commission</span>
            </div>
            <div className="text-xl font-bold text-purple-600">
              {service.commission || partner.generalCommission}%
            </div>
            <div className="text-sm text-gray-600">
              {service.commission ? 'spécifique' : 'générale'}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Onglets */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'general', label: 'Informations générales', icon: Settings },
                { id: 'images', label: 'Galerie Photos', icon: Camera },
                { id: 'availability', label: 'Disponibilités', icon: Calendar },
                { id: 'conditions', label: 'Conditions', icon: AlertCircle }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Onglet Informations générales */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Détails de la prestation</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la prestation</label>
                        {editMode.general ? (
                          <input
                            type="text"
                            value={editingService.name}
                            onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <div className="font-medium text-gray-800">{service.name}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        {editMode.general ? (
                          <textarea
                            value={editingService.description}
                            onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        ) : (
                          <div className="text-gray-800">{service.description}</div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prix (€)</label>
                          {editMode.general ? (
                            <input
                              type="number"
                              value={editingService.price}
                              onChange={(e) => setEditingService({...editingService, price: Number(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">€{service.price.toLocaleString()}</div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Durée</label>
                          {editMode.general ? (
                            <input
                              type="text"
                              value={editingService.duration}
                              onChange={(e) => setEditingService({...editingService, duration: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">{service.duration}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Commission</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Taux de commission (%)</label>
                        {editMode.general ? (
                          <div className="space-y-3">
                            <input
                              type="number"
                              value={editingService.commission || ''}
                              onChange={(e) => setEditingService({...editingService, commission: e.target.value ? Number(e.target.value) : null})}
                              placeholder={`Défaut: ${partner.generalCommission}%`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              step="0.1"
                              min="0"
                              max="100"
                            />
                            <div className="text-xs text-gray-500">
                              Laissez vide pour utiliser la commission générale ({partner.generalCommission}%)
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="font-medium text-gray-800">
                              {service.commission ? (
                                <span className="text-blue-600">{service.commission}% (spécifique)</span>
                              ) : (
                                <span className="text-gray-600">{partner.generalCommission}% (générale)</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              Commission par prestation: €{((service.commission || partner.generalCommission) * service.price / 100).toFixed(0)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Galerie Photos */}
            {activeTab === 'images' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Galerie Photos ({serviceImages.length})
                  </h3>
                  <button
                    onClick={handleImageUpload}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Upload size={18} />
                    <span>Ajouter Photos</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {serviceImages.map((image) => (
                    <div key={image.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="relative">
                        <img 
                          src={image.url} 
                          alt={image.alt}
                          className="w-full h-48 object-cover"
                        />
                        {image.isPrimary && (
                          <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                            Photo principale
                          </div>
                        )}
                        <div className="absolute top-2 right-2 flex space-x-1">
                          {!image.isPrimary && (
                            <button
                              onClick={() => handleSetPrimaryImage(image.id)}
                              className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-75"
                              title="Définir comme photo principale"
                            >
                              <Star size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteImage(image.id)}
                            className="bg-black bg-opacity-50 text-white p-1 rounded hover:bg-opacity-75"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-medium text-gray-800">{image.title}</h4>
                        <p className="text-sm text-gray-600">{image.alt}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Disponibilités */}
            {activeTab === 'availability' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Horaires et Disponibilités</h3>
                  {!editMode.availability && (
                    <button
                      onClick={() => handleToggleEdit('availability')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Edit size={16} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="space-y-4">
                    {days.map((day) => {
                      const availability = editMode.availability 
                        ? editingAvailability[day.key] 
                        : service.availability[day.key];
                      
                      return (
                        <div key={day.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="font-medium text-gray-800 w-32">{day.label}</div>
                          
                          <div className="flex-1 mx-4">
                            {editMode.availability ? (
                              <div className="space-y-2">
                                <div className="flex items-center space-x-4">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={availability.available}
                                      onChange={(e) => handleAvailabilityChange(day.key, 'available', e.target.checked)}
                                      className="rounded border-gray-300 text-blue-600 focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Disponible</span>
                                  </label>
                                </div>
                                {availability.available && (
                                  <input
                                    type="text"
                                    value={availability.slots.join(', ')}
                                    onChange={(e) => handleSlotsChange(day.key, e.target.value)}
                                    placeholder="Ex: 09:00-12:00, 14:00-18:00"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                  />
                                )}
                              </div>
                            ) : (
                              <div>
                                {availability.available ? (
                                  <div className="flex flex-wrap gap-2">
                                    {availability.slots.map((slot, index) => (
                                      <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                                        {slot}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-red-600 text-sm">Fermé</span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <span className={`w-3 h-3 rounded-full ${availability.available ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {editMode.availability && (
                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        onClick={() => handleSave('availability')}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Save size={16} />
                        <span>Sauvegarder</span>
                      </button>
                      <button
                        onClick={() => handleCancel('availability')}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        <X size={16} />
                        <span>Annuler</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet Conditions */}
            {activeTab === 'conditions' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Conditions et Restrictions</h3>
                  {!editMode.conditions && (
                    <button
                      onClick={() => handleToggleEdit('conditions')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Edit size={16} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-800 mb-4">Restrictions d'âge et de capacité</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Âge minimum</label>
                          {editMode.conditions ? (
                            <input
                              type="number"
                              value={editingService.minAge}
                              onChange={(e) => setEditingService({...editingService, minAge: Number(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="0"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">
                              {service.minAge > 0 ? `${service.minAge} ans` : 'Aucune restriction'}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Âge maximum</label>
                          {editMode.conditions ? (
                            <input
                              type="number"
                              value={editingService.maxAge || ''}
                              onChange={(e) => setEditingService({...editingService, maxAge: e.target.value ? Number(e.target.value) : null})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="0"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">
                              {service.maxAge ? `${service.maxAge} ans` : 'Aucune restriction'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Minimum de personnes</label>
                          {editMode.conditions ? (
                            <input
                              type="number"
                              value={editingService.minGuests}
                              onChange={(e) => setEditingService({...editingService, minGuests: Number(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="1"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">{service.minGuests} personnes</div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Maximum de personnes</label>
                          {editMode.conditions ? (
                            <input
                              type="number"
                              value={editingService.maxGuests}
                              onChange={(e) => setEditingService({...editingService, maxGuests: Number(e.target.value)})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="1"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">{service.maxGuests} personnes</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-800 mb-4">Conditions particulières</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes et conditions</label>
                        {editMode.conditions ? (
                          <textarea
                            value={editingService.conditions}
                            onChange={(e) => setEditingService({...editingService, conditions: e.target.value})}
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Conditions particulières, équipements requis, restrictions..."
                          />
                        ) : (
                          <div className="text-gray-800 bg-white p-3 rounded border">
                            {service.conditions || 'Aucune condition particulière'}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${service.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm font-medium text-gray-700">
                          Statut: {service.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {editMode.conditions && (
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => handleSave('conditions')}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Save size={16} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('conditions')}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <X size={16} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;